import { CODES, KEYS } from "../keys";
import { register } from "./register";
import {
  copyTextToSystemClipboard,
  copyToClipboard,
  createPasteEvent,
  probablySupportsClipboardBlob,
  probablySupportsClipboardWriteText,
  readSystemClipboard,
} from "../clipboard";
import clipboard from "clipboardy";
import { actionDeleteSelected } from "./actionDeleteSelected";
import { exportCanvas, prepareElementsForExport } from "../data/index";
import { isTextElement } from "../element";
import { t } from "../i18n";
import { isFirefox } from "../constants";
import {activeNode, copy, emitter, EmitterEvents, mindMapCache, mindMapPngCache} from "../element/mindMap";
import { getFormValue } from "./actionProperties";
import {postMessage, recvMessage} from "../../../../src/common/event";
import {
  EVENT_MINDMAP_ADD_CARD,
  EVENT_MINDMAP_ADD_IMAGE,
  EVENT_MINDMAP_ADD_LINK, EVENT_MINDMAP_ADD_PDF, EVENT_MINDMAP_DELETE_CARD,
  EVENT_MINDMAP_LINE_COLOR,
  EVENT_MINDMAP_LINE_COLOR_OK, EVENT_MINDMAP_REMOVE_ATTACH,
  EVENT_WINDOW_BAR_VISIBLE
} from "../../../../src/Const";
import mitt from "mitt";
import {useEffect} from "react";
import {hsvaToHex, rgbaToHex, rgbStringToHsva} from "@uiw/react-color";
import {rgbToHex} from "../colors";
import {isStringNullOrEmpty} from "../../../../src/utils";
import i18next from "i18next";
import Global from "../../../../src/global";
import {transformMarkdownTo} from "../components/MindMap/Plus/markdownTo.js";
import { message } from "antd";
import { getTextFromHtml } from "simple-mind-map/src/utils";
import {isImageElement, isLinearElement} from "../element/typeChecks";
import {getBoundTextElement} from "../element/textElement";
import markdown from "simple-mind-map/src/parse/markdown.js";

export const actionCopy = register({
  name: "copy",
  trackEvent: { category: "element" },
  perform:  (elements, appState, event: ClipboardEvent | null, app) => {
    const selectedElements = app.scene.getSelectedElements({
      selectedElementIds: appState.selectedElementIds,
      includeBoundTextElement: true,
      includeElementsInFrames: true,
    });

    //保存对应关系。。。
    sessionStorage.setItem(Global.g_currentBoardId,JSON.stringify(appState.selectedElementIds));


    // 兼容：框选时可能不会把连接线选中（selection逻辑会过滤掉），
    // 但用户期望复制/粘贴后连接线仍然存在。
    // 因此：如果一条连接线的 startBinding/endBinding 两端都绑定在已选中的元素上，
    // 即便该连接线本身未被选中，也将其一并加入复制内容。
    const selectedIds = new Set(selectedElements.map((el) => el.id));
    const connectedLinearElements = app.scene
        .getNonDeletedElements()
        .filter((el) => {
          if (!isLinearElement(el)) {
            return false;
          }
          const startId = el.startBinding?.elementId;
          const endId = el.endBinding?.elementId;
          return !!startId && !!endId && selectedIds.has(startId) && selectedIds.has(endId);
        });

    // If we auto-include a connector, we must also include its bound text label
    // (arrow/line text) otherwise the pasted connector will render without text.
    const connectedLinearBoundTextElements = connectedLinearElements
        .map((el) => getBoundTextElement(el))
        .filter((el): el is NonNullable<typeof el> => !!el);

    const elementsToCopy = Array.from(
        new Map(
            [
              ...selectedElements,
              ...connectedLinearElements,
              ...connectedLinearBoundTextElements,
            ].map((el) => [el.id, el]),
        ).values(),
    );

    // 复制操作，保存数值的数据
    if (elementsToCopy.length === 1) {
      const element = elementsToCopy[0];
      if (isImageElement(element)) {
        copy.clipboard = {
          event,
          element,
          file: app.files[element.fileId!],
        };
      }
    }

    try {
      copyToClipboard(elementsToCopy, app.files, event);
    } catch (error: any) {
      return {
        commitToHistory: false,
        appState: {
          ...appState,
          errorMessage: error.message,
        },
      };
    }

    return {
      commitToHistory: false,
    };
  },
  contextItemLabel: "labels.copy",
  // don't supply a shortcut since we handle this conditionally via onCopy event
  keyTest: undefined,
});

export const actionPaste = register({
  name: "paste",
  trackEvent: { category: "element" },
  perform: async (elements, appState, data, app) => {
    let types;
    try {
      types = await readSystemClipboard();
    } catch (error: any) {
      if (error.name === "AbortError" || error.name === "NotAllowedError") {
        // user probably aborted the action. Though not 100% sure, it's best
        // to not annoy them with an error message.
        return false;
      }

      console.error(`actionPaste ${error.name}: ${error.message}`);

      if (isFirefox) {
        return {
          commitToHistory: false,
          appState: {
            ...appState,
            errorMessage: t("hints.firefox_clipboard_write"),
          },
        };
      }

      return {
        commitToHistory: false,
        appState: {
          ...appState,
          errorMessage: t("errors.asyncPasteFailedOnRead"),
        },
      };
    }

    try {
      app.pasteFromClipboard(createPasteEvent({ types }));
    } catch (error: any) {
      console.error(error);
      return {
        commitToHistory: false,
        appState: {
          ...appState,
          errorMessage: t("errors.asyncPasteFailedOnParse"),
        },
      };
    }

    return {
      commitToHistory: false,
    };
  },
  contextItemLabel: "labels.paste",
  // don't supply a shortcut since we handle this conditionally via onCopy event
  keyTest: undefined,
});

export const actionCut = register({
  name: "cut",
  trackEvent: { category: "element" },
  perform: (elements, appState, event: ClipboardEvent | null, app) => {
    actionCopy.perform(elements, appState, event, app);
    setTimeout(()=>{
      app.focusContainer();
    },200)
    return actionDeleteSelected.perform(elements, appState);
  },
  contextItemLabel: "labels.cut",
  keyTest: (event) => event[KEYS.CTRL_OR_CMD] && event.key === KEYS.X,
});

export const actionCopyAsSvg = register({
  name: "copyAsSvg",
  trackEvent: { category: "element" },
  perform: async (elements, appState, _data, app) => {
    if (!app.canvas) {
      return {
        commitToHistory: false,
      };
    }

    const { exportedElements, exportingFrame } = prepareElementsForExport(
      elements,
      appState,
      true,
    );

    try {
      await exportCanvas(
        "clipboard-svg",
        exportedElements,
        appState,
        app.files,
        {
          ...appState,
          exportingFrame,
        },
      );
      return {
        commitToHistory: false,
        appState: {
          ...appState,
          toast: {
            message: t("toast.copyToClipboardAsSvg1"),
          },
        },

      };
    } catch (error: any) {
      console.error(error);
      return {
        appState: {
          ...appState,
          errorMessage: error.message,
        },
        commitToHistory: false,
      };
    }
  },
  predicate: (elements) => {
    return probablySupportsClipboardWriteText && elements.length > 0;
  },
  contextItemLabel: "labels.copyAsSvg",
});

export const actionCopyAsPng = register({
  name: "copyAsPng",
  trackEvent: { category: "element" },
  perform: async (elements, appState, _data, app) => {
    if (!app.canvas) {
      return {
        commitToHistory: false,
      };
    }
    const selectedElements = app.scene.getSelectedElements({
      selectedElementIds: appState.selectedElementIds,
      includeBoundTextElement: true,
      includeElementsInFrames: true,
    });

    const { exportedElements, exportingFrame } = prepareElementsForExport(
      elements,
      appState,
      true,
        true
    );
    try {
      await exportCanvas("clipboard", exportedElements, appState, app.files, {
        ...appState,
        exportingFrame,
      });
      return {
        appState: {
          ...appState,
          toast: {
            message: t("toast.copyToClipboardAsPng1", {
              exportSelection: selectedElements.length
                ? t("toast.selection")
                : t("toast.canvas"),
              exportColorScheme: appState.exportWithDarkMode
                ? ""
                : "",
            }),
          },
        },
        commitToHistory: false,
      };
    } catch (error: any) {
      console.error(error);
      return {
        appState: {
          ...appState,
          errorMessage: error.message,
        },
        commitToHistory: false,
      };
    }
  },
  predicate: (elements) => {
    return probablySupportsClipboardBlob && elements.length > 0;
  },
  contextItemLabel: "labels.copyAsPng",
  keyTest: (event) => event.code === CODES.C && event.altKey && event.shiftKey,
});

export const copyText = register({
  name: "copyText",
  trackEvent: { category: "element" },
  perform: (elements, appState, _, app) => {
    const selectedElements = app.scene.getSelectedElements({
      selectedElementIds: appState.selectedElementIds,
      includeBoundTextElement: true,
    });

    const text = selectedElements
      .reduce((acc: string[], element) => {
        if (isTextElement(element)) {
          acc.push(element.text);
        }
        return acc;
      }, [])
      .join("\n\n");
    copyTextToSystemClipboard(text);
    return {
      commitToHistory: false,
    };
  },
  predicate: (elements, appState, _, app) => {
    return (
      probablySupportsClipboardWriteText &&
      app.scene
        .getSelectedElements({
          selectedElementIds: appState.selectedElementIds,
          includeBoundTextElement: true,
        })
        .some(isTextElement)
    );
  },
  contextItemLabel: "labels.copyText",
});

export const actionAddImage = register({
  name: "addImage",
  trackEvent: { category: "element" },
  perform: (elements, appState, event: ClipboardEvent | null, app) => {
    emitter.emit("mindmapAddImage", { });
    return {
      appState: {
        ...appState,
        // mindmap: {
        //   ...appState.mindmap,
        //   modal: {
        //     visible: true,
        //     type: "image",
        //   },
        // },
      },
      commitToHistory: false,
    };
  },
  contextItemLabel: "Add image",
  icon:'context_menu_image'
});

export const actionMindmapAddVideo = register({
  name: "mindmapAddVideo",
  trackEvent: { category: "element" },
  perform: (elements, appState, event: ClipboardEvent | null, app) => {
    emitter.emit("mindmapAddVideo", { });
    return {
      appState: {
        ...appState,
      },
      commitToHistory: false,
    };
  },
  contextItemLabel: "Add video",
  icon:'context_menu_video'
});
export const actionMindmapRemoveVideo = register({
  name: "mindmapRemoveVideo",
  trackEvent: { category: "element" },
  perform: (elements, appState, event: ClipboardEvent | null, app) => {
    const { element, node } = activeNode;
    if (node){
      node.setHyperlink(null);
    }
    return {
      appState: {
        ...appState,
      },
      commitToHistory: false,
    };
  },
  contextItemLabel: "Remove video",
  icon:'context_menu_video'
});

export const actionMindmapAddFile = register({
  name: "mindmapAddFile",
  trackEvent: { category: "element" },
  perform: (elements, appState, event: ClipboardEvent | null, app) => {
    emitter.emit("mindmapAddFile", { });
    return {
      appState: {
        ...appState,
      },
      commitToHistory: false,
    };
  },
  contextItemLabel: "Add file",
  icon:'context_menu_file'
});
export const actionMindmapRemoveFile = register({
  name: "mindmapRemoveFile",
  trackEvent: { category: "element" },
  perform: (elements, appState, event: ClipboardEvent | null, app) => {
    const { element, node } = activeNode;
    if (node){
      node.setHyperlink(null);
    }

    return {
      appState: {
        ...appState,
      },
      commitToHistory: false,
    };
  },
  contextItemLabel: "Remove file",
  icon:'context_menu_file'
});

export const actionMindmapAddHighlight = register({
  name: "mindmapAddHighlight",
  trackEvent: { category: "element" },
  perform: (elements, appState, event: ClipboardEvent | null, app) => {
    emitter.emit("mindmapAddHighlight", { });
    return {
      appState: {
        ...appState,
      },
      commitToHistory: false,
    };
  },
  contextItemLabel: "Add highlight",
  icon:'context_menu_highlight'
});
export const actionMindmapRemoveHighlight = register({
  name: "mindmapRemoveHighlight",
  trackEvent: { category: "element" },
  perform: (elements, appState, event: ClipboardEvent | null, app) => {
    const { element, node } = activeNode;
    if (node){
      node.setHyperlink(null);
    }
    return {
      appState: {
        ...appState,
      },
      commitToHistory: false,
    };
  },
  contextItemLabel: "Remove highlight",
  icon:'context_menu_highlight'
});

export const actionMindmapCopylink = register({
  name: "mindmapCopylink",
  trackEvent: { category: "element" },
  perform: (elements, appState, event: ClipboardEvent | null, app) => {
    emitter.emit("mindmapCopylink", { });
    return {
      appState: {
        ...appState,
      },
      commitToHistory: false,
    };
  },
  contextItemLabel: "Copy link",
  icon:'context_menu_add_link'
});

export const actionMindmapAddPdf = register({
  name: "mindmapAddPdf",
  trackEvent: { category: "element" },
  perform: (elements, appState, event: ClipboardEvent | null, app) => {
    postMessage(EVENT_MINDMAP_ADD_PDF,{});
    return {
      appState: {
        ...appState,
      },
      commitToHistory: false,
    };
  },
  contextItemLabel: ("Add pdf"),
  icon:'context_menu_pdf'
});

export const actionMindmapAddCard = register({
  name: "mindmapAddCard",
  trackEvent: { category: "element" },
  perform: (elements, appState, event: ClipboardEvent | null, app) => {
    postMessage(EVENT_MINDMAP_ADD_CARD,{});
    return {
      appState: {
        ...appState,
      },
      commitToHistory: false,
    };
  },
  contextItemLabel: ("Add card"),
  icon:'context_menu_card'
});

export const actionMindmapDeleteCard = register({
  name: "mindmapRemoveCard",
  trackEvent: { category: "element" },
  perform: (elements, appState, event: ClipboardEvent | null, app) => {
    // postMessage(EVENT_MINDMAP_DELETE_CARD,{});
    const { element, node } = activeNode;
    if (node){
      node.setAttachment('');
    }

    return {
      appState: {
        ...appState,
      },
      commitToHistory: false,
    };
  },
  contextItemLabel: ("Remove card"),
  icon:'context_menu_card'
});

export const actionMindmapRemoveLink = register({
  name: "mindmapRemoveLink",
  trackEvent: { category: "element" },
  perform: (elements, appState, event: ClipboardEvent | null, app) => {
    const { element, node } = activeNode;
    if (node){
      node.setHyperlink(null);
      node.setAttachment('');
    }
    return {
      appState: {
        ...appState,
      },
      commitToHistory: false,
    };
  },
  contextItemLabel: "Remove link",
  icon:'context_menu_remove_link'
});

export const actionAddLink = register({
  name: "addLink",
  trackEvent: { category: "element" },
  perform: (elements, appState, event: ClipboardEvent | null, app) => {
    postMessage(EVENT_MINDMAP_ADD_LINK,{linkInfo:appState.mindmap.selected.node?.nodeData?.data.hyperlink});
    return {
      appState: {
        ...appState,
        // mindmap: {
        //   ...appState.mindmap,
        //   modal: {
        //     visible: true,
        //     type: "link",
        //   },
        // },
      },
      commitToHistory: false,
    };
  },
  contextItemLabel: ("Add link"),
  icon:'context_menu_add_link'
});

export const actionEditLink = register({
  name: "editLink",
  trackEvent: { category: "element" },
  perform: (elements, appState, event: ClipboardEvent | null, app) => {
    let szHyperLink = appState.mindmap.selected.node?.nodeData?.data.hyperlink;
    if (!szHyperLink){
      szHyperLink = Global.g_currentSelectMindmap?.nodeData?.data.hyperlink;
    }
    postMessage(EVENT_MINDMAP_ADD_LINK,{linkInfo:szHyperLink});
    return {
      appState: {
        ...appState,
        // mindmap: {
        //   ...appState.mindmap,
        //   modal: {
        //     visible: true,
        //     type: "link",
        //   },
        // },
      },
      commitToHistory: false,
    };
  },
  contextItemLabel: "Edit link",
  icon:'context_menu_add_link'
});

export const actionMoveUpNode = register({
  name: "moveUpNode",
  trackEvent: { category: "element" },
  perform: (elements, appState, event: ClipboardEvent | null, app) => {
    const { element, node } = activeNode;

    const currentMindMap = mindMapCache.get(element.id)!;
    currentMindMap.execCommand("UP_NODE");

    return {
      appState: {
        ...appState,
      },
      commitToHistory: false,
    };
  },
  contextItemLabel: "Move up node",
  icon:'context_menu_move_up'
});

export const actionChangeMindmapLineColor = register({
  name: "mindmapChangeLineColor",
  trackEvent: { category: "element" },
  perform: (elements, appState, event: ClipboardEvent | null, app) => {

    const { element, node } = activeNode;
    let lineColor = node.getStyle("lineColor");
    if (lineColor === "transparent") {
      lineColor = "#ffffff";
    }

    const mindMap = mindMapCache.get(element.id);
    if (!lineColor) {
      lineColor = mindMap
          ? // @ts-ignore
          mindMap.rainbowLines.getNodeColor(node)
          : "#ffffff";
    }
    if (!lineColor){
      lineColor = "#ffffff";
    }
    if (lineColor.startsWith('rgb')){
      const hsva = rgbStringToHsva(lineColor);
      lineColor = hsvaToHex(hsva);
    }

    postMessage(EVENT_MINDMAP_LINE_COLOR,{color:lineColor});
    // useEffect(()=>{
      recvMessage(EVENT_MINDMAP_LINE_COLOR_OK,(msg,data)=>{
        node.setStyle("lineColor", data.data.color);
      },true)
    // })


    return {
      appState: {
        ...appState,
      },
      commitToHistory: false,
    };
  },
  contextItemLabel: "Line color",
  icon:'context_menu_line_color'
});

export const actionMoveDownNode = register({
  name: "moveDownNode",
  trackEvent: { category: "element" },
  perform: (elements, appState, event: ClipboardEvent | null, app) => {
    const { element, node } = activeNode;

    const currentMindMap = mindMapCache.get(element.id)!;
    currentMindMap.execCommand("DOWN_NODE");

    return {
      appState: {
        ...appState,
      },
      commitToHistory: false,
    };
  },
  contextItemLabel: "Move down node",
  icon:'context_menu_move_down'
});

export const actionDeleteNode = register({
  name: "deleteNode",
  trackEvent: { category: "element" },
  perform: (elements, appState, event: ClipboardEvent | null, app) => {
    const { element, node } = activeNode;
    const currentMindMap = mindMapCache.get(element.id)!;
    if (node?.isRoot || node?.getData()?.extra){

      const event = new KeyboardEvent("keydown", {
        key: KEYS.DELETE,
        code: KEYS.DELETE,
        bubbles: true, // 事件是否冒泡
        cancelable: true, // 事件是否可以被取消
      });

      document
          .getElementsByClassName("excalidraw-container")?.[0]
          ?.dispatchEvent(event);

      //有附加的card/pdf?
      if (!node.isRoot){
        //root的删除在另外的地方....
        //app 搜索  window.showMessageBox(i18next.t("Tips"),i18next.t("De
        postMessage(EVENT_MINDMAP_REMOVE_ATTACH,{mindMap:currentMindMap,node:node,command:'REMOVE_NODE'})
      }

    }else{
      currentMindMap.execCommand("REMOVE_NODE");
    }

    return {
      appState: {
        ...appState,
        mindmapOperations:{
          deletingElements:[element.id]
        }
      },
      commitToHistory: false,
    };
  },
  contextItemLabel: "Delete node",
  icon:'context_menu_delete'
});



export const actionMindmapImportFromMarkdown = register({
  name: "mindmapImportFromMarkdown",
  trackEvent: { category: "element" },
  perform: (elements, appState, event: ClipboardEvent | null, app) => {

    const { element, node } = activeNode;
    const mindMap = mindMapCache.get(element.id)!;
    window.showDialogImportMindmapFromMd(async (mdData)=>{
      let data = await transformMarkdownTo(mdData, element.id);
      //Markdown 属于结构变化，需要标记dirty;
      try {
        (mindMap as any).__excalidrawDataDirty = true;
        (mindMap as any).__excalidrawMaybeSelectionRender = false;
      } catch (error) {}
      mindMap.setData(data);
      window.closeDialogImportMindmapFromMd();
    })

    return {
      appState: {
        ...appState,
      },
      commitToHistory: false,
    };
  },
  contextItemLabel: "ImportFromMarkdown",
  icon:'context_menu_from_markdown.svg'
});


/**
 * 这个只有菜单。不做什么用.....
 */
export const actionMindmapExportToMarkdown = register({
  name: "mindmapExportToMarkdown",
  trackEvent: { category: "element" },
  perform: (elements, appState, event: ClipboardEvent | null, app) => {

    const { element, node } = activeNode;

    return {
      appState: {
        ...appState,
      },
      commitToHistory: false,
    };
  },
  contextItemLabel: "ExportToMarkdown",
  icon:'context_menu_import_to_markdown.svg'
});


export const actionMindmapExportAsMarkdown = register({
  name: "mindmapExportAsMarkdown",
  trackEvent: { category: "element" },
  perform: (elements, appState, event: ClipboardEvent | null, app) => {

    const { element, node } = activeNode;
    const mindMap = mindMapCache.get(element.id)!;
    const rootName = getTextFromHtml(
        mindMap.renderer.root.nodeData.data.text,
    );
    mindMap.export("md", true, rootName);

    return {
      appState: {
        ...appState,
      },
      commitToHistory: false,
    };
  },
  contextItemLabel: "ExportAsMarkdownFile",
  icon:'context_menu_import_to_markdown.svg'
});

export const actionMindmapCopyToClipboardAsMarkdown = register({
  name: "mindmapCopyToClipboardAsMarkdown",
  trackEvent: { category: "element" },
  perform: (elements, appState, event: ClipboardEvent | null, app) => {

    const { element, node } = activeNode;
    const mindMap = mindMapCache.get(element.id)!;
    const data = mindMap.getData();
    const textToCopy = markdown.transformToMarkdown(data);
    clipboard.write(textToCopy);
    message.info(i18next.t('Copied to clipboard'));
    return {
      appState: {
        ...appState,
      },
      commitToHistory: false,
    };
  },
  contextItemLabel: "CopyToClipboardAsMarkdown",
  icon:'context_menu_copy_to_clipboard_as_markdown.svg'
});